import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  FaArrowRight,
  FaSearch,
  FaStar,
  FaUsers,
} from "react-icons/fa";
import Footer from "../common/Footer";
import Spinner from "../common/Spinner";
import GetAvgRating from "../utils/avgRating";
import { getAllCourses } from "../services/operations/courseDetailsAPI";
import coursesBanner from "../assets/Brand/explore-courses-banner.png";
import "./AllCourses.css";

const levels = ["All", "Beginner", "Intermediate", "Advanced", "Stratergy", "Free"];

const CourseCard = ({ course }) => {
  const rating = GetAvgRating(course.ratingAndReviews || []);
  const instructor = `${course.instructor?.firstName || ""} ${
    course.instructor?.lastName || ""
  }`.trim();
  const skills = Array.isArray(course.tag) ? course.tag.slice(0, 3) : [];

  return (
    <article className="all-course-card">
      <Link to={`/courses/${course._id}`} className="all-course-image">
        <img src={course.thumbnail} alt={course.courseName} />
        <span>{course.courseType}</span>
      </Link>
      <div className="all-course-body">
        {instructor && <small>By {instructor}</small>}
        <Link to={`/courses/${course._id}`}>
          <h3>{course.courseName}</h3>
        </Link>
        <p>{course.courseDescription}</p>
        {skills.length > 0 && (
          <div className="all-course-skills">
            <b>Skills:</b>
            {skills.map((skill) => (
              <span key={skill}>{skill}</span>
            ))}
          </div>
        )}
        <div className="all-course-meta">
          <span><FaStar /> {rating.toFixed(1)}</span>
          <span><FaUsers /> {course.studentsEntrolled?.length || 0} learners</span>
        </div>
        <div className="all-course-bottom">
          <strong>₹{course.price}</strong>
          <Link to={`/courses/${course._id}`}>
            View details <FaArrowRight />
          </Link>
        </div>
      </div>
    </article>
  );
};

const AllCourses = () => {
  const [searchParams] = useSearchParams();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [level, setLevel] = useState("All");
  const [category, setCategory] = useState("All");
  const [query, setQuery] = useState(() => searchParams.get("search") || "");

  useEffect(() => {
    setQuery(searchParams.get("search") || "");
  }, [searchParams]);

  useEffect(() => {
    getAllCourses()
      .then((data) => setCourses(Array.isArray(data) ? data : []))
      .finally(() => setLoading(false));
  }, []);

  const categories = useMemo(
    () => [
      "All",
      ...new Set(courses.map((course) => course.category?.name).filter(Boolean)),
    ],
    [courses]
  );

  const filteredCourses = useMemo(() => {
    const search = query.trim().toLowerCase();
    return courses.filter((course) => {
      const matchesLevel = level === "All" || course.courseType === level;
      const matchesCategory =
        category === "All" || course.category?.name === category;
      const matchesSearch =
        !search ||
        `${course.courseName || ""} ${course.courseDescription || ""} ${
          course.tag?.join(" ") || ""
        }`
          .toLowerCase()
          .includes(search);
      return matchesLevel && matchesCategory && matchesSearch;
    });
  }, [category, courses, level, query]);

  const groupedCourses = useMemo(() => {
    return filteredCourses.reduce((groups, course) => {
      const name = course.category?.name || "Other Courses";
      if (!groups[name]) groups[name] = [];
      groups[name].push(course);
      return groups;
    }, {});
  }, [filteredCourses]);

  return (
    <main className="all-courses-page">
      <section className="all-courses-image-hero">
        <img src={coursesBanner} alt="Read the charts and build your trading strategy" />
      </section>

      <section className="all-courses-controls">
        <div className="all-courses-wrap">
          <div className="all-course-levels" aria-label="Browse courses by level">
            {levels.map((item) => (
              <button
                key={item}
                className={level === item ? "active" : ""}
                onClick={() => setLevel(item)}
              >
                {item === "Stratergy" ? "Strategy" : item}
              </button>
            ))}
          </div>
          <label className="all-course-search">
            <FaSearch />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search courses or skills"
            />
          </label>
        </div>
      </section>

      <section className="all-courses-content">
        <div className="all-courses-wrap all-courses-layout">
          <aside>
            <h2>Filter by category</h2>
            {categories.map((item) => (
              <button
                key={item}
                className={category === item ? "active" : ""}
                onClick={() => setCategory(item)}
              >
                <span>{item}</span>
                <b>
                  {item === "All"
                    ? courses.length
                    : courses.filter((course) => course.category?.name === item)
                        .length}
                </b>
              </button>
            ))}
          </aside>

          <div className="all-courses-results">
            {loading ? (
              <div className="all-courses-loading"><Spinner /></div>
            ) : filteredCourses.length === 0 ? (
              <div className="all-courses-empty">
                <h2>No matching courses</h2>
                <p>Try another level, category, or search term.</p>
                <button
                  onClick={() => {
                    setLevel("All");
                    setCategory("All");
                    setQuery("");
                  }}
                >
                  Clear filters
                </button>
              </div>
            ) : (
              Object.entries(groupedCourses).map(([name, items]) => (
                <section key={name} className="all-course-group">
                  <div className="all-course-group-title">
                    <h2>{name}</h2>
                    <span>{items.length} course{items.length === 1 ? "" : "s"}</span>
                  </div>
                  <div className="all-course-grid">
                    {items.map((course) => (
                      <CourseCard key={course._id} course={course} />
                    ))}
                  </div>
                </section>
              ))
            )}
          </div>
        </div>
      </section>
      <Footer />
    </main>
  );
};

export default AllCourses;
